import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ToolbarService } from '../../services/toolbar.service';
import { Message } from '../../models/message';
import { HomeDelivery } from '../../models/home-delivery';
import { EventService } from '../../services/event.service';
import { LocationService } from '../../services/location.service';
import { LoggingService } from '../../services/logging.service';
import { MessageService } from '../../services/message.service';
import { ScannerService } from '../../services/scanner.service';
import { TranslateService } from '@ngx-translate/core';
import { TourService } from '../../services/tour.service';
import { Subscription } from 'rxjs';
import { Address } from '../../models/address';
import { BeepService } from '../../services/beep.service';
import { ModalService } from '../../services/modal.service';
import { AError } from '../../models/aerror';
import { AppUrls } from '../../app.urls';
import { isNullOrUndefined } from "../../utils";
import {LocationAwareComponent} from '../location-aware/location-aware.component';

@Component({
  selector: 'app-home-delivery-details',
  templateUrl: './home-delivery-details.component.html',
  styleUrls: ['./home-delivery-details.component.scss']
})
export class HomeDeliveryDetailsComponent extends LocationAwareComponent implements OnInit, OnDestroy {

  // set class on host element
  @HostBinding('class') classes = 'app-content';

  homeDelivery: HomeDelivery;
  private hasFetchedHomeDelivery = false;

  private canLeave = false;

  private guideMessage: Message;
  private successMessage: Message;

  private scanResultSubscription: Subscription;

  constructor(
    private location: Location,
    private router: Router,
    private toolbar: ToolbarService,
    private events: EventService,
    private logger: LoggingService,
    private messages: MessageService,
    private changeDetectorRef: ChangeDetectorRef,
    private scanner: ScannerService,
    private beep: BeepService,
    private tourService: TourService,
    private modal: ModalService,
    private translate: TranslateService
  ) {
    super();

    // load home delivery from navigation state (deserialization necessary, can persist across reloads)
    const state = this.router.getCurrentNavigation()?.extras.state;
    if (state['homeDelivery']) {
      try {
        this.homeDelivery = HomeDelivery.fromJSON(state['homeDelivery']);
      } catch (e) {
        // intentionally swallowed
      }
    }

    if (!this.homeDelivery) {
      console.error(`No home delivery passed`);
      this.router.navigate([AppUrls.ROOT, AppUrls.Home], { replaceUrl: true });
      throw Error('No home delivery passed!');
    }
    this.hasFetchedHomeDelivery = false;
    this.logger.log('Showing home delivery details', this.homeDelivery);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.canLeave = false;
    this.toolbar.setToolbarConfig({
      backAction: () => this.onBack(),
      showRefresh: true,
      showSettings: true,
      showCall: true,
      showLogout: false
    });

    this.showGuideMessage(this.translate.instant('homeDeliveryDetails.scanItems'));

    this.scanResultSubscription = this.scanner.scanResult.subscribe(scanResult => {
      this.scanner.pauseScanning();
      this.tryToScanItem(scanResult.data)
        .then(isSuccessful => isSuccessful ? this.beep.beep() : this.beep.bop());
      // scanner might be either paused or resumed here
    });
    this.setupScanner();
    this.fetchStatus();
  }

  override ngOnDestroy(): void {
    if (this.guideMessage) {
      this.messages.remove(this.guideMessage);
    }
    if (this.successMessage) {
      this.messages.remove(this.successMessage);
    }
    this.scanResultSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  private onBack(): void {
    if (this.canLeave || this.homeDelivery.status.scanned < 1) {
      this.location.back();
      return;
    }

    this.messages.add(Message.warning(this.translate.instant('homeDeliveryDetails.leavePrompt')), 2000);
  }

  // ===== View helpers ===== //

  public openAddress(address: Address): void {
    this.locationService.showDirectionsTo(address);
  }

  public done(): void {
    if (this.homeDelivery.isComplete) {
      // normal case: all items were scanned
      this.finishHomeDelivery()
        .then(() => {
          this.leaveHomeDelivery();
        })
        .catch(error => {
          this.showErrorMessage(AError.fromSomeError(error).humanMessage);
        });
    } else {
      // exceptional case: not all items have been scanned yet
      this.scanner.pauseScanning();
      if (this.homeDelivery.isPickup) {

        // in pickup, we allow explicitly specifying that there are less items than expected
        this.modal.showConfirmationModal(
          undefined,
          this.translate.instant('homeDeliveryDetails.lowItemCountPrompt', { itemCount: this.homeDelivery.status.scanned }),
          this.translate.instant('yes'),
          this.translate.instant('no')
        ).then(result => {
          if (result) {
            // user confirms that delivery really only consists of the scanned items, finish it now
            this.finishHomeDelivery().then(() => {
              this.leaveHomeDelivery();
            }).catch(error => {
              this.showErrorMessage(AError.fromSomeError(error).humanMessage);
              this.scanner.resumeScanning();
            });
          } else {
            // user indicates that delivery does not only consist of the scanned items
            this.showNotAllItemsScannedModal().then(ok => {
              if (ok) {
                // user wishes to finish anyway
                this.leaveHomeDelivery();
              } else {
                // user cancelled
                this.scanner.resumeScanning();
              }
            });
          }
        });
      } else if (this.homeDelivery.isHandover) {
        // Handover (ZUS), less items than in the order (Mindermengen) are not supported here
        this.showNotAllItemsScannedModal().then(ok => {
          if (ok) {
            this.leaveHomeDelivery();
          } else {
            // user cancelled
            this.scanner.resumeScanning();
          }
        });
      } else {
        // shouldn't happen
        this.scanner.resumeScanning();
      }
    }
  }

  private showNotAllItemsScannedModal(): Promise<boolean> {
    return this.modal.showConfirmationModal(
      undefined,
      this.translate.instant('homeDeliveryDetails.notAllScannedPrompt'),
      this.translate.instant('ok'),
      this.translate.instant('cancel')
    );
  }

  get showIncreaseItemOption(): boolean {
    return this.hasFetchedHomeDelivery
      && this.homeDelivery
      && this.homeDelivery.isPickup // increasing items is only possible when picking up
      && this.homeDelivery.isComplete // and when all other items have been scanned
      && !this.homeDelivery.isBatchScanPossible; // not applicable to batch scanning
  }

  increaseItemCount(): void {
    this.scanner.pauseScanning();
    this.modal.showIncreaseItemCountModal(this.homeDelivery)
      .then(newItemCount => {
        if (newItemCount) {
          return this.tourService.increaseHomeDeliveryItemCount(this.homeDelivery, newItemCount);
        } else {
          return Promise.resolve(undefined); // cancel
        }
      })
      .then(itemStatus => {
        if (itemStatus) {
          this.showSuccessMessage(this.translate.instant('homeDeliveryDetails.itemCountIncreased'), 2000);
        }
      })
      .catch(error => {
        this.showErrorMessage(AError.fromSomeError(error).humanMessage);
      })
      .finally(() => {
        this.scanner.resumeScanning();
      });
  }

  // ===== Message helpers ===== //

  private showGuideMessage(text: string): void {
    if (this.guideMessage) {
      this.messages.remove(this.guideMessage);
      this.messages.removeMultiple(this.messages.warnings);
    }

    this.guideMessage = Message.info(text);
    this.messages.add(this.guideMessage);
  }

  private showSuccessMessage(text: string, removeAfter?: number): void {
    if (this.successMessage) {
      this.messages.remove(this.successMessage);
      this.messages.removeMultiple(this.messages.warnings);
    }

    this.successMessage = Message.success(text);
    if (removeAfter) {
      this.messages.add(this.successMessage, removeAfter);
    } else {
      this.messages.add(this.successMessage);
    }
  }

  private showErrorMessage(text: string): void {
    if (this.successMessage) {
      this.messages.remove(this.successMessage);
    }
    this.messages.add(Message.warning(text));
  }

  private leaveHomeDelivery(): void {
    this.canLeave = true;
    this.onBack();
  }

  // ===== Home delivery (order/tour stop) operations ===== //

  private finishHomeDelivery(): Promise<void> {
    return this.tourService.finishHomeDelivery(this.homeDelivery);
  }

  private fetchStatus(): void {
    this.tourService.getHomeDeliveryStatus(this.homeDelivery).then(homeDelivery => {
      this.homeDelivery = homeDelivery;
      this.hasFetchedHomeDelivery = true;
      this.setupScanner();
    });
  }

  private tryToScanItem(barcode: string): Promise<boolean> {

    if (this.homeDelivery.isBatchScanPossible) {
      this.showBatchScanningDialog(barcode);
      // scanner is paused now, keep it that way (no resume here)
      return Promise.resolve(true);
    } else {

      // single item scanning
      return this.tourService.scanHomeDeliveryItem(barcode, this.homeDelivery)
        .then(homeDelivery => {
          this.homeDelivery = homeDelivery;
          this.showSuccessMessage(this.translate.instant('homeDeliveryDetails.scannedItem', { barcode }));
          this.changeDetectorRef.detectChanges();
          if (!this.homeDelivery.isComplete) {
            this.scanner.resumeScanning();
          }
          return true;
        })
        .catch(someError => {
          this.showErrorMessage(AError.fromSomeError(someError).humanMessage);
          this.scanner.resumeScanning();
          return Promise.resolve(false);
        });
    }
  }

  // ===== Private helpers ===== //

  private setupScanner(): void {
    if (this.homeDelivery.isComplete) {
      this.scanner.pauseScanning();
    } else {
      this.scanner.resumeScanning();
    }
  }

  private showBatchScanningDialog(barcode: string): void {
    this.modal.showBatchScanningModal(this.homeDelivery)
      .then(actualNumberOfItems => {
        if (isNullOrUndefined(actualNumberOfItems)) {
          return Promise.resolve(undefined); // cancel
        } else {
          return this.tourService.batchScanHomeDelivery(barcode, this.homeDelivery, actualNumberOfItems);
        }
      })
      .then(contractStatus => {
        if (contractStatus) {
          this.showSuccessMessage(this.translate.instant('homeDeliveryDetails.finished'), 2000);
        } else {
          this.scanner.resumeScanning();
        }
      })
      .catch(error => {
        this.showErrorMessage(AError.fromSomeError(error).humanMessage);
        this.scanner.resumeScanning();
      });
  }
}
